import type { ReactNode } from 'react';

interface PassportSelfViewFloorProps {
  /**
   * The advanced-analysis tier flag this section carries on the CROSS-PERSON
   * Passport. Recorded so the pairing stays visible at the call site and so a
   * reader can see which gate is being floored, not merely that one was.
   */
  tierFeature: string;
  /**
   * One line: what substantive fact about the subject this section shows, and
   * which other audience already sees it. Required — the floor applies to
   * EXISTING facts exposed elsewhere, and a wrapper without that justification
   * is just a deleted feature gate.
   */
  fact: string;
  children: ReactNode;
}

/**
 * CORE-C — the constitutional self-view floor.
 *
 * ============================================================================
 * THE RULE
 * ============================================================================
 * Product decision, 2026-09-13:
 *
 *   If OptiBay already holds a substantive fact about a person and the Passport
 *   exposes that fact to another human audience, the subject must be able to
 *   see that fact on their own Passport.
 *
 *   Self-view is a visibility FLOOR, not a feature-entitlement elevator.
 *
 * It restates CORE-C's own SHALL — "present, to the person, every substantive
 * fact the Passport exposes to any other human audience" — and settles which
 * side wins when a presentation tier gate collides with it.
 *
 * ============================================================================
 * WHY THIS EXISTS RATHER THAN JUST DELETING THE GATE
 * ============================================================================
 * The asymmetry is not hypothetical and it is not "the shop did not pay".
 * Feature flags come from the ACTIVE SHOP's tier (`AuthContext` →
 * `mapShopTierToPlanTier(shop.tier)`), and **with no active shop they fall back
 * to the CORE baseline**. So a technician reading their own Passport in
 * passport mode — precisely the portable-record case the Passport exists for —
 * got the CORE baseline and saw nothing, while a manager at their shop, with
 * that shop active, saw the whole record. Same facts, same product, hidden from
 * their subject only.
 *
 * Deleting the gates outright would have said nothing about why. This wrapper
 * makes each exemption a named, reviewable claim with its justification beside
 * it, and keeps them findable in one grep.
 *
 * ============================================================================
 * WHAT IT IS NOT
 * ============================================================================
 * It does not activate premium workflows, generate premium intelligence, expose
 * manager controls, shop or team context, or anyone else's information; it does
 * not bypass authentication, top-level Passport availability, server
 * authorization or identity ownership; and it never manufactures a fact. If the
 * underlying fact does not exist, the section renders its own absent state
 * exactly as before.
 *
 * Verified before use, for every section floored here: the read path performs
 * no write and triggers no premium generation — the four hooks involved contain
 * zero insert/update/upsert/delete calls and `kpi_passport_summary` contains no
 * INSERT/UPDATE/DELETE. A tier may still govern whether an owning capability
 * GENERATES the fact; this governs only whether CORE-C HIDES one that exists.
 *
 * ============================================================================
 * WHERE IT MAY BE USED
 * ============================================================================
 * `PassportHome` only. That page resolves its subject from
 * `useAuth().user.id` and takes no route parameter, so it is self-only BY
 * CONSTRUCTION — there is no other person for a floor to leak to. On
 * `/technicians/:id` the audience contract decides, and it is untouched:
 * a SERVICE_WRITER, manager or owner gains nothing from this component.
 */
export function PassportSelfViewFloor({ children }: PassportSelfViewFloorProps) {
  return <>{children}</>;
}
